import { native } from '@forest/native-services'
import {
  createPaywallApi,
  createUserInfoSource,
  FailureCauseError,
  failureCause,
  type UserInfo,
} from '@forest/paywall-core'
import type { Loaded } from '~/lib/loaded'

async function getNativeUserInfo(): Promise<UserInfo> {
  const result = await native.user.getUserInfo()
  if (!result.success) {
    throw new FailureCauseError('native_failed', 'Failed to get user info')
  }
  if (!result.data) {
    throw new FailureCauseError('no_user', 'Native has no user info')
  }
  return result.data
}

/** The user's credentials, read from native once and refreshed on a 401. */
export const userInfoSource = createUserInfoSource({
  load: getNativeUserInfo,
  refresh: async () => {
    const result = await native.user.refreshToken()
    if (!result.success) {
      throw new FailureCauseError(
        'token_refresh_failed',
        'Failed to refresh token',
      )
    }
    return result.data
  },
})

/**
 * The user's info, read fresh from native into the user-info source, at
 * bootstrap, or why native could not provide it.
 */
export async function loadUserInfo(): Promise<Loaded<UserInfo>> {
  try {
    const userInfo = await getNativeUserInfo()
    userInfoSource.atom.set(userInfo)
    return { ok: true, value: userInfo }
  } catch (error) {
    return { ok: false, cause: failureCause(error) }
  }
}

/**
 * The Service API, the Receipt System API and the Infra API, for the purchase
 * flow.
 */
export const api = createPaywallApi({ userInfo: userInfoSource })
